import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  App,
  Form,
  Row,
  Col,
  InputNumber,
  Switch,
  Select,
  Spin,
  Button,
} from "antd";
import { StarOutlined, StarFilled } from "@ant-design/icons";

import useTrackSignals from "hooks/useTrackSignals";
import CreateOptionStrategyModal from "components/OptionsStrategies/CreateOptionStrategyModal";

import TradingReadinessCard from "components/Cards/Options/TradingReadinessCard";
import OptionsSuccessRateCard from "components/Cards/Options/OptionsSuccessRateCard";
import EdgeCard from "components/Cards/Options/EdgeCard";
import ExpectedMoveCard from "components/Cards/Options/ExpectedMoveCard";
import PortfolioSimulationCard from "components/Cards/Options/PortfolioSimulationCard";
import YearlyEdgeChart from "components/Cards/Options/YearlyEdgeChart";
import HitDayDistributionChart from "components/Cards/Options/HitDayDistributionChart";
import ExcursionCard from "components/Cards/Options/ExcursionCard";
import ReturnDistributionCard from "components/Cards/Options/ReturnDistributionCard";
import EdgeRobustnessCard from "components/Cards/Options/EdgeRobustnessCard";
import StabilityCard from "components/Cards/Options/StabilityCard";
import FrequencyCard from "components/Cards/Options/FrequencyCard";
import CalendarTradabilityCard from "components/Cards/Options/CalendarTradabilityCard";
import ExpectancyCard from "components/Cards/Options/ExpectancyCard";
import AverageBarPathChart from "components/Cards/Options/AverageBarPathChart";
import RealizedGainHistogram from "components/Cards/Options/RealizedGainHistogram";
import RollingSuccessRateChart from "components/Cards/Options/RollingSuccessRateChart";
import OptionsSignalTable from "./OptionsSignalTable";

import { apiAnalyzeOptionsSignals } from "api/apiPredictions";

const WEEKDAY_OPTIONS = [
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
];

const OptionsSignalSection = (props) => {
  const { results, onTrackingChanged } = props;
  const { message } = App.useApp();

  // Carried over verbatim when this tab was opened from an Options Leaderboard
  // row ("Analyze" button) so the analysis view reflects exactly what was
  // ranked, instead of re-deriving defaults from the model.
  const initialParams = results.initialParams;

  const [targetGainPercent, setTargetGainPercent] = useState(
    initialParams?.targetGainPercent ??
      +((results.model.percToGain - 1) * 100).toFixed(2),
  );
  const [minDays, setMinDays] = useState(initialParams?.minDays ?? 2);
  const [maxDays, setMaxDays] = useState(
    initialParams?.maxDays ?? Math.min(Math.max(results.model.days || 5, 2), 5),
  );

  const [mwfExpiriesEnabled, setMwfExpiriesEnabled] = useState(
    initialParams?.mwfExpiriesEnabled ?? false,
  );
  const [excludedEntryWeekdays, setExcludedEntryWeekdays] = useState(
    initialParams?.excludedEntryWeekdays ?? [],
  );
  const [optionsResults, setOptionsResults] = useState();
  const [isLoading, setIsLoading] = useState(false);

  const { isTracked, toggleTracking } = useTrackSignals({
    onChanged: onTrackingChanged,
  });
  const [isTogglingTracking, setIsTogglingTracking] = useState(false);
  const tracked = isTracked(results.symbol, results.model._id);

  const onToggleTracking = async () => {
    setIsTogglingTracking(true);
    try {
      const added = await toggleTracking(
        results.symbol,
        results.model._id,
        results.startDate,
        { targetGainPercent, minDays, maxDays, excludedEntryWeekdays },
      );
      message.success(
        added
          ? `Tracking ${results.symbol} - ${results.model.name} signals`
          : `Stopped tracking ${results.symbol} - ${results.model.name}`,
      );
    } catch (err) {
      message.error(
        err?.response?.data?.message ||
          err.message ||
          "Failed to update tracking",
      );
    } finally {
      setIsTogglingTracking(false);
    }
  };

  useEffect(() => {
    if (minDays > maxDays || targetGainPercent <= 0) return;

    const controller = new AbortController();

    const timeout = setTimeout(async () => {
      setIsLoading(true);
      try {
        const result = await apiAnalyzeOptionsSignals(
          {
            symbol: results.symbol,
            models: [results.model._id],
            startDate: results.startDate,
            endDate: results.endDate,
            targetGainPercent,
            minDays,
            maxDays,
            mwfExpiriesEnabled,
            excludedEntryWeekdays,
          },
          controller.signal,
        );
        setOptionsResults(result);
      } catch (err) {
        if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
          message.error(
            err?.response?.data?.message || "Failed to analyze signals",
          );
        }
      } finally {
        setIsLoading(false);
      }
    }, 200);

    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [
    results.symbol,
    results.model._id,
    results.startDate,
    results.endDate,
    targetGainPercent,
    minDays,
    maxDays,
    mwfExpiriesEnabled,
    excludedEntryWeekdays,
    message,
  ]);

  // Same params the signal analysis above runs with -- the portfolio
  // simulation is computed server-side from them, so it needs the full
  // request rather than the signals themselves.
  const signalRequest = useMemo(
    () => ({
      symbol: results.symbol,
      models: [results.model._id],
      startDate: results.startDate,
      endDate: results.endDate,
      targetGainPercent,
      minDays,
      maxDays,
      mwfExpiriesEnabled,
      excludedEntryWeekdays,
    }),
    [
      results.symbol,
      results.model._id,
      results.startDate,
      results.endDate,
      targetGainPercent,
      minDays,
      maxDays,
      mwfExpiriesEnabled,
      excludedEntryWeekdays,
    ],
  );

  const isRangeInvalid = useMemo(() => minDays > maxDays, [minDays, maxDays]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <div
          className="flex items-center"
          style={{ justifyContent: "space-between" }}
        >
          <h3>Signal Parameters</h3>
          <div className="flex items-center gap-2">
            {/* a signals-only options strategy */}
            <Button
              size="small"
              type={tracked ? "primary" : "default"}
              icon={tracked ? <StarFilled /> : <StarOutlined />}
              loading={isTogglingTracking}
              onClick={onToggleTracking}
            >
              {tracked ? "Tracking signals" : "Track signals"}
            </Button>
            <CreateOptionStrategyModal
              symbol={results.symbol}
              model={results.model}
              startDate={results.startDate}
              analysisParams={{
                targetGainPercent,
                minDays,
                maxDays,
                excludedEntryWeekdays,
              }}
            />
          </div>
        </div>
        <Form layout="vertical">
          <Row gutter={16}>
            <Col>
              <Form.Item
                label="Target gain %"
                tooltip="Minimum underlying move to count a signal as a success"
              >
                <InputNumber
                  min={0.1}
                  step={0.5}
                  value={targetGainPercent}
                  onChange={setTargetGainPercent}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                label="Min bars"
                tooltip="Shortest weekly DTE you'd realistically trade, in trading bars (not calendar days)"
                validateStatus={isRangeInvalid ? "error" : undefined}
              >
                <InputNumber
                  min={1}
                  step={1}
                  value={minDays}
                  onChange={setMinDays}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                label="Max bars"
                tooltip="Longest weekly DTE you'd realistically trade, in trading bars (not calendar days)"
                validateStatus={isRangeInvalid ? "error" : undefined}
                help={
                  isRangeInvalid ? "Max bars must be >= min bars" : undefined
                }
              >
                <InputNumber
                  min={1}
                  step={1}
                  value={maxDays}
                  onChange={setMaxDays}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                label="Mon/Wed/Fri expiries"
                tooltip="Some symbols now offer weekly expiries every Mon/Wed/Fri instead of just Friday. Enabling this checks whether a real expiry actually exists within your min/max bar window -- off assumes Friday-only weeklies."
              >
                <Switch
                  checked={mwfExpiriesEnabled}
                  onChange={setMwfExpiriesEnabled}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                label="Exclude entry weekdays"
                tooltip="Don't open a signal when the buy prediction lands on these weekdays -- e.g. exclude Wed/Thu/Fri to only test Mon/Tue entries."
              >
                <Select
                  mode="multiple"
                  allowClear
                  style={{ minWidth: 220 }}
                  placeholder="None excluded"
                  options={WEEKDAY_OPTIONS}
                  value={excludedEntryWeekdays}
                  onChange={setExcludedEntryWeekdays}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>

      {isLoading && !optionsResults && (
        <div className="flex justify-center pt-4">
          <Spin tip="Analyzing signals..." />
        </div>
      )}

      {optionsResults && (
        <Spin spinning={isLoading} tip="Recalculating...">
          <div className="cards-wrapper">
            <TradingReadinessCard
              optionsResults={optionsResults}
              isCorrupted={results.isCorrupted}
            />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <h3>Edge</h3>
            <div className="cards-wrapper">
              <OptionsSuccessRateCard optionsResults={optionsResults} />
              <EdgeCard optionsResults={optionsResults} />
              <ExpectedMoveCard optionsResults={optionsResults} />
            </div>
            <YearlyEdgeChart optionsResults={optionsResults} />
            <HitDayDistributionChart optionsResults={optionsResults} />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <h3>Win Quality</h3>
            <div className="cards-wrapper">
              <ExpectancyCard optionsResults={optionsResults} />
              <EdgeRobustnessCard optionsResults={optionsResults} />
              <ExcursionCard optionsResults={optionsResults} />
              <ReturnDistributionCard optionsResults={optionsResults} />
            </div>
            <AverageBarPathChart optionsResults={optionsResults} />
            <RealizedGainHistogram optionsResults={optionsResults} />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <h3>Trust &amp; Tradability</h3>
            <div className="cards-wrapper">
              <StabilityCard optionsResults={optionsResults} />
              <CalendarTradabilityCard optionsResults={optionsResults} />
              <FrequencyCard optionsResults={optionsResults} />
            </div>
            <RollingSuccessRateChart optionsResults={optionsResults} />
          </div>

          <div className="pt-4">
            <OptionsSignalTable signals={optionsResults.signals} />
          </div>

          <div className="flex flex-col gap-2 pt-4">
            <h3>Portfolio Simulation (test)</h3>
            <PortfolioSimulationCard
              optionsResults={optionsResults}
              signalRequest={signalRequest}
            />
          </div>
        </Spin>
      )}
    </div>
  );
};

OptionsSignalSection.propTypes = {
  results: PropTypes.object.isRequired,
  // called after Track signals creates or deletes a strategy
  onTrackingChanged: PropTypes.func,
};

export default OptionsSignalSection;
